const COOKIE_CONSENT_STORAGE_KEY = "_cookie_consent";

export const isLocalDevelopmentHost = () => {
	if (typeof window === "undefined") return false;

	const { hostname } = window.location;
	return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local");
};

export const isCookiebotEnabled = () => {
	if (typeof window === "undefined") return false;
	return Boolean(window.COOKIEBOT_ID);
};

const getCookiebot = () => {
	if (typeof window === "undefined") return null;
	return window.Cookiebot || null;
};

export const hasStatisticsConsent = () => {
	const cb = getCookiebot();
	return Boolean(cb?.consent?.statistics);
};

export const hasMarketingConsent = () => {
	const cb = getCookiebot();
	return Boolean(cb?.consent?.marketing);
};

export const hasPreferencesConsent = () => {
	const cb = getCookiebot();
	return Boolean(cb?.consent?.preferences);
};

const hasCookiebotResponse = () => {
	const cb = getCookiebot();
	return Boolean(cb?.hasResponse);
};

const isCookiebotAccepted = () => {
	const cb = getCookiebot();
	if (!cb) return false;
	return Boolean(cb.hasResponse && !cb.declined && cb.consent?.statistics);
};

const isDefaultAccepted = () => {
	if (typeof window === "undefined" || !window.localStorage) return false;
	return window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === "true";
};

const isDefaultResolved = () => {
	if (typeof window === "undefined" || !window.localStorage) return false;
	const value = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
	return value === "true" || value === "false";
};

const isCookieFeatureEnabled = () => {
	if (typeof window === "undefined") return false;
	return (
		isCookiebotEnabled() ||
		window.HAS_COOKIE_POLICY_DRAWER === true ||
		window.HAS_COOKIE_POLICY_DRAWER === "true"
	);
};

const isCookiebotLoadFailedInternal = () => {
	if (typeof window === "undefined") return false;
	return window.__cookiebotLoadFailed === true;
};

export const getCookieConsent = () => {
	if (!isCookieFeatureEnabled()) return true;

	if (isCookiebotEnabled()) {
		// Cookiebot is domain-bound and often blocked locally (ad blockers, wrong host).
		if (
			isLocalDevelopmentHost() &&
			(!hasCookiebotResponse() || isCookiebotLoadFailedInternal())
		) {
			return true;
		}

		return isCookiebotAccepted();
	}

	return isDefaultAccepted();
};

export const isBettoGameplayCookieBypassEnabled = () =>
	typeof window !== "undefined" && window.TENANT === "betto";

export const hasGameplayCookieConsent = () =>
	isBettoGameplayCookieBypassEnabled() || getCookieConsent();

export const cookieConsentWasResolved = () => {
	if (!isCookieFeatureEnabled()) return true;

	if (isCookiebotEnabled()) {
		if (
			isLocalDevelopmentHost() &&
			(!hasCookiebotResponse() || isCookiebotLoadFailedInternal())
		) {
			return true;
		}

		return hasCookiebotResponse();
	}

	return isDefaultResolved();
};

export const openCookieSettings = () => {
	if (isCookiebotEnabled()) {
		getCookiebot()?.renew?.();
		return;
	}

	if (typeof window !== "undefined" && typeof window.__toggleCookieConsent === "function") {
		window.__toggleCookieConsent(true);
	}
};

export const runCookiebotScripts = () => {
	getCookiebot()?.runScripts?.();
};

const COOKIEBOT_CONSENT_EVENTS = [
	"CookiebotOnConsentReady",
	"CookiebotOnAccept",
	"CookiebotOnDecline",
];

export const onCookiebotConsentReady = (callback) => {
	if (typeof window === "undefined" || typeof callback !== "function") {
		return () => {};
	}
	COOKIEBOT_CONSENT_EVENTS.forEach((event) => {
		window.addEventListener(event, callback);
	});
	return () => {
		COOKIEBOT_CONSENT_EVENTS.forEach((event) => {
			window.removeEventListener(event, callback);
		});
	};
};

export const isCookiebotLoadFailed = () => isCookiebotLoadFailedInternal();

export const COOKIEBOT_LOAD_FAILED_EVENT = "cookiebot-load-failed";

export const onCookiebotLoadFailed = (handler) => {
	if (typeof window === "undefined") return () => {};

	if (isCookiebotLoadFailed()) {
		handler({ detail: "already-failed" });
	}

	window.addEventListener(COOKIEBOT_LOAD_FAILED_EVENT, handler);
	return () => window.removeEventListener(COOKIEBOT_LOAD_FAILED_EVENT, handler);
};

export const reloadPageForCookiebotRetry = () => {
	if (typeof window === "undefined") return;
	window.__cookiebotLoadFailed = false;
	window.location.reload();
};
